import { useEffect, useState } from "react";

import { ExpressionNode } from "@/components/expression/ExpressionNode";
import { SelectedContext } from "@/components/expression/utils/layout/SelectedContext";
import { NodeStyleProvider } from "@/components/expression/utils/NodeStyleProvider";
import { getSuggestionNode } from "@/components/suggestions/providers/utils";
import {
  insertNodeChildren,
  setNodeDecorators,
  setNodeChildren,
} from "@/components/suggestions/utils";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { nodeFromJson } from "@/models/nodes/nodeFromJson";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { isPowerOperator } from "@/models/utils/semanticChecks";

import type { NodeRenderContext } from "@/components/expression/utils/NodeStyleProvider";
import type { Suggestion, MultiSuggestion, SingleSuggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson } from "@corca/data";

function buildSingleNodeFromSingleSuggest(
  suggestion: SingleSuggestion,
  doc: DocumentModel,
): NodeModel | undefined {
  const [node] = getSuggestionNode(suggestion, doc) ?? [];

  if (!node) {
    return undefined;
  }

  insertNodeChildren(
    doc,
    node,
    suggestion.item.insertChildren || suggestion.substitution?.insertChildren,
  );
  setNodeDecorators(
    doc,
    node,
    suggestion.item.insertDecorators || suggestion.substitution?.insertDecorators,
  );
  setNodeChildren(doc, node, suggestion.item.setChildren || suggestion.substitution?.setChildren);

  return node;
}

// TODO: move this logic to multinode provider
export function buildSingleNodeFromMultiSuggest(
  suggestion: MultiSuggestion,
  doc: DocumentModel,
  inlineNode: InlineNodeModel,
  index = 0,
): { index: number; nodes: NodeModel[] } {
  let nestedNode: InlineNodeModel = inlineNode;
  let topIndex = index;
  const canAppend = index === inlineNode.nodes.length;
  const ancestors = [inlineNode];
  const addedNodes: NodeModel[] = [];

  for (const item of suggestion.items) {
    const node = buildSingleNodeFromSingleSuggest({ type: "single", item, priority: 1 }, doc);

    if (!node) {
      break;
    }

    // very hacky!
    if (nestedNode !== inlineNode) {
      if (node.is(FunctionNodeModel) || (node.is(VariableNodeModel) && node.name === "Function")) {
        const isAfterIntegral =
          nestedNode.parent?.is(FunctionNodeModel) && nestedNode.parent.name.includes("Integral");
        const isVariableInDifferential =
          nestedNode.parent?.is(FunctionNodeModel) &&
          nestedNode.parent.name.includes("Differential") &&
          node.is(VariableNodeModel);

        if (!isAfterIntegral && !isVariableInDifferential) {
          nestedNode = ancestors.pop() ?? inlineNode;
        }
      }

      if (isPowerOperator(node) && nestedNode.nodes.length === 0) {
        const funcParent = nestedNode.parent;
        // sin^2(x): power on the function name, following items stay inside the parentheses
        if (funcParent?.is(FunctionNodeModel) && funcParent.powerPlacement === "child") {
          funcParent.prependChild(node);
          continue;
        }
        nestedNode = ancestors.pop() ?? inlineNode;
      }
    }

    if (canAppend || nestedNode !== inlineNode) {
      nestedNode.appendChild(node);
    } else {
      nestedNode.nodes[topIndex].insertSiblingBefore(node);
    }

    if (nestedNode === inlineNode) {
      topIndex += 1;
    }

    if (node.is(FunctionNodeModel) || (node.is(VariableNodeModel) && node.name === "Function")) {
      if (node.is(VariableNodeModel) && !node.hasParams) {
        node.appendChild(doc.create(InlineNodeModel));
      }
      ancestors.push(nestedNode);
      nestedNode = node.parameters[0];
    }

    if (nestedNode === inlineNode) {
      addedNodes.push(node);
    }
  }

  for (const node of inlineNode.descendants) {
    if (node.is(VariableNodeModel)) {
      if (node.parameters.length === 1 && node.parameters[0].isEmpty) {
        if (node.power) {
          doc.removeNode(node.power);
        }
        doc.removeNode(node.parameters[0]);
      }
    }
  }

  return { index: topIndex, nodes: addedNodes };
}

export function ExpressionRealNodePreview({
  renderContext = "editor",
  suggestion,
  node,
  doc,
}: {
  renderContext?: NodeRenderContext;
  suggestion?: Suggestion;
  node?: NodeModel;
  doc: DocumentModel;
}) {
  const [builtNode, setBuiltNode] = useState<NodeModel | null>(null);
  useEffect(() => {
    // FIXME: doc.transact/doc.create can trigger re-render, better prepare node in suggestion provider
    if (!suggestion) {
      setBuiltNode(null);
      return;
    }
    if (suggestion.type === "multi") {
      const n = doc.transact(() => {
        const inlineNode = doc.create(InlineNodeModel);
        buildSingleNodeFromMultiSuggest(suggestion, doc, inlineNode);
        return inlineNode;
      }, "suggestionPreview:multi");
      setBuiltNode(n);
    }
    if (suggestion.type === "single") {
      const n = doc.transact(() => {
        if (suggestion.item.nodesJson) {
          const nodes = suggestion.item.nodesJson
            .map((sn) => nodeFromJson(doc, sn))
            .filter(Boolean);
          const inlineNode = doc.create(InlineNodeModel, nodes);

          return inlineNode;
        }

        return buildSingleNodeFromSingleSuggest(suggestion, doc);
      }, "suggestionPreview:single");

      setBuiltNode(n ?? null);
    }
  }, [doc, suggestion]);

  const nodePlaceholders =
    suggestion?.type === "single" ? (suggestion.item.placeholders ?? null) : null;

  const nodeToRender = node || builtNode;

  const labelPlaceholderVisible =
    suggestion?.type === "single" ? suggestion.item.labelPlaceholderVisible : "if-empty";

  if (!nodeToRender) {
    return null;
  }

  return (
    <NodeStyleProvider
      renderContext={renderContext}
      placeholderVisible="if-empty"
      labelPlaceholderVisible={labelPlaceholderVisible ?? "if-empty"}
      size="sm"
      view="compact-expression"
      computedSplit={suggestion && insertsReferenceNode(suggestion)}
    >
      <SelectedContext.Provider value={false}>
        <ExpressionNode
          node={nodeToRender}
          nodeRenderProps={nodePlaceholders ? { placeholders: nodePlaceholders } : undefined}
          className="pointer-events-none font-mono"
        />
      </SelectedContext.Provider>
    </NodeStyleProvider>
  );
}

/**
 * A reference suggestion previews the whole `formula = result` expression but
 * inserts a `reference` node. When detected, the preview renders as a computed
 * equation so the result part gets the computed-split outline.
 */
function insertsReferenceNode(suggestion: Suggestion): boolean {
  const containsReference = (node: NodeJson): boolean => {
    return node.type === "computedReference" || (node.children?.some(containsReference) ?? false);
  };

  if (suggestion.type !== "single" || suggestion.action?.type !== "insert-expression") {
    return false;
  }
  return suggestion.action.nodesJson.some(containsReference);
}
